import { type Component, getKeybindings, type TUI, truncateToWidth } from "@elyracode/tui";
import { theme } from "../theme/theme.js";

/** Replace tabs with spaces for consistent column rendering. */
function replaceTabs(text: string): string {
	return text.replace(/\t/g, "    ");
}

/**
 * Colorize a raw unified-diff (git diff) line by its leading marker.
 * Returns the styled line.
 */
function colorizeGitDiffLine(line: string): string {
	const expanded = replaceTabs(line);
	// Hunk header: @@ -a,b +c,d @@
	if (expanded.startsWith("@@")) {
		return theme.fg("accent", expanded);
	}
	// File headers and metadata.
	if (
		expanded.startsWith("diff --git") ||
		expanded.startsWith("index ") ||
		expanded.startsWith("+++") ||
		expanded.startsWith("---") ||
		expanded.startsWith("new file") ||
		expanded.startsWith("deleted file") ||
		expanded.startsWith("rename ") ||
		expanded.startsWith("similarity ") ||
		expanded.startsWith("Binary files")
	) {
		return theme.bold(theme.fg("muted", expanded));
	}
	if (expanded.startsWith("+")) {
		return theme.fg("toolDiffAdded", expanded);
	}
	if (expanded.startsWith("-")) {
		return theme.fg("toolDiffRemoved", expanded);
	}
	return theme.fg("toolDiffContext", expanded);
}

export interface DiffViewerCallbacks {
	/** Open the diff in the external editor. */
	onOpenEditor: () => void;
	/** Close the viewer. */
	onClose: () => void;
}

/**
 * Full-height, scrollable diff viewer rendered in the editor area.
 *
 * Keys:
 * - up/down: scroll one line
 * - PageUp/PageDown: scroll one viewport
 * - g / G (or Home / End): jump to top / bottom
 * - e: open the diff in the external editor
 * - Esc / q: close
 */
export class DiffViewerComponent implements Component {
	private readonly styledLines: string[];
	private scrollOffset = 0;
	private lastViewport = 10;

	constructor(
		private tui: TUI,
		diffText: string,
		private title: string,
		private callbacks: DiffViewerCallbacks,
	) {
		this.styledLines = diffText.replace(/\n$/, "").split("\n").map(colorizeGitDiffLine);
	}

	invalidate(): void {
		// No cached layout state.
	}

	private getViewportHeight(): number {
		// Reserve rows for: title, top rule, bottom rule, hint line, and a small margin.
		const reserved = 5;
		const rows = this.tui.terminal.rows || 24;
		return Math.max(5, rows - reserved);
	}

	private clampScroll(viewport: number): void {
		const maxOffset = Math.max(0, this.styledLines.length - viewport);
		if (this.scrollOffset < 0) this.scrollOffset = 0;
		if (this.scrollOffset > maxOffset) this.scrollOffset = maxOffset;
	}

	render(width: number): string[] {
		const viewport = this.getViewportHeight();
		this.lastViewport = viewport;
		this.clampScroll(viewport);

		const total = this.styledLines.length;
		const start = this.scrollOffset;
		const end = Math.min(start + viewport, total);

		const lines: string[] = [];

		// Title
		lines.push(theme.bold(theme.fg("accent", truncateToWidth(this.title, width))));

		// Top rule with position indicator
		const above = start;
		const topLabel = above > 0 ? `─── ↑ ${above} more ` : "";
		lines.push(theme.fg("muted", truncateToWidth(topLabel + "─".repeat(Math.max(0, width)), width)));

		// Visible content
		for (let i = start; i < end; i++) {
			lines.push(truncateToWidth(this.styledLines[i], width));
		}

		// Bottom rule with position indicator
		const below = total - end;
		const bottomLabel = below > 0 ? `─── ↓ ${below} more ` : "";
		lines.push(theme.fg("muted", truncateToWidth(bottomLabel + "─".repeat(Math.max(0, width)), width)));

		// Hint line
		lines.push(
			theme.fg(
				"dim",
				truncateToWidth(" ↑↓ scroll · PgUp/PgDn page · g/G top/bottom · e open in editor · Esc close", width),
			),
		);

		return lines;
	}

	handleInput(keyData: string): void {
		const kb = getKeybindings();
		const viewport = this.lastViewport;

		if (kb.matches(keyData, "tui.select.up")) {
			this.scrollOffset -= 1;
		} else if (kb.matches(keyData, "tui.select.down")) {
			this.scrollOffset += 1;
		} else if (kb.matches(keyData, "tui.select.pageUp")) {
			this.scrollOffset -= viewport;
		} else if (kb.matches(keyData, "tui.select.pageDown")) {
			this.scrollOffset += viewport;
		} else if (keyData === "g" || keyData === "\x1b[H") {
			// g or Home → top
			this.scrollOffset = 0;
		} else if (keyData === "G" || keyData === "\x1b[F") {
			// G or End → bottom
			this.scrollOffset = this.styledLines.length;
		} else if (keyData === "e") {
			this.callbacks.onOpenEditor();
			return;
		} else if (kb.matches(keyData, "tui.select.cancel") || keyData === "q") {
			this.callbacks.onClose();
			return;
		} else {
			return;
		}

		this.clampScroll(viewport);
		this.tui.requestRender();
	}
}
